import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";

import Button from "../../../components/Button";
import IconButton from "../../../components/IconButton";
import Input from "../../../components/Input";
import Loader from "../../../components/Loader";
import { Select } from "../../../components/OldSelect";
import { AuthContext } from "../../../contexts/AuthContext";
import { DeleteV2Icon } from "../../../assets/img/icons";
import {
    getAppSettings,
    patchAppSettings,
} from "../../../services/api/admin/settings";
import { JournalSettings } from "../../../types/api/admin/settings";
import { useUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import {
    FieldErrors,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";

import styles from "../Progression/styles.module.scss";
import localStyles from "./styles.module.scss";

type Props = {
    handleTabChange: (key: string) => void;
};

function journalSnapshot(
    demoAccountBase: string,
    options: number[],
    challenge: string
): string {
    const cleaned = options.filter((n) => Number.isFinite(n) && n > 0);
    return JSON.stringify({
        demoAccountBase,
        options: cleaned,
        challenge,
    });
}

const Journal: FC<Props> = ({ handleTabChange }) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const { hasPermission } = useContext(AuthContext)!;
    const { registerUnsavedGuard } = useUnsavedChanges();
    const canReadSettings = hasPermission("admin-settings", "read");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [demoAccountBase, setDemoAccountBase] = useState("50000");
    const [options, setOptions] = useState<number[]>([150, 300, 500]);
    const [challenge, setChallenge] = useState("2500");
    const [newOption, setNewOption] = useState("");
    const [baseline, setBaseline] = useState<string | null>(null);
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    useEffect(() => {
        getAppSettings()
            .then((settings) => {
                if (!settings?.journal) {
                    throw new Error("EMPTY_SETTINGS");
                }
                const journal: JournalSettings = settings.journal;
                const base = String(journal.demoAccountBase);
                const opts = [...journal.moneyManagementOptions].sort(
                    (a, b) => a - b
                );
                const challengeValue = String(journal.challengeMoneyManagement);
                setDemoAccountBase(base);
                setOptions(opts);
                setChallenge(challengeValue);
                setBaseline(journalSnapshot(base, opts, challengeValue));
            })
            .catch(() => {
                toast.error("Impossible de charger les paramètres.");
            })
            .finally(() => setIsLoading(false));
    }, []);

    const isDirty = useMemo(() => {
        if (baseline === null) return false;
        return (
            journalSnapshot(demoAccountBase, options, challenge) !== baseline
        );
    }, [baseline, demoAccountBase, options, challenge]);

    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);

    const handleAddOption = () => {
        const value = Number(newOption);
        if (!Number.isFinite(value) || value <= 0) {
            toast.warn("Saisis un montant positif.");
            return;
        }
        if (value === Number(challenge)) {
            toast.warn("Ce montant est réservé au Challenge.");
            return;
        }
        if (options.includes(value)) {
            toast.warn("Cette option existe déjà.");
            return;
        }
        setOptions((prev) => [...prev, value]);
        setNewOption("");
        setFieldErrorsState((prev) =>
            clearFieldError(prev, "moneyManagementOptions")
        );
    };

    const handleOptionChange = (index: number, raw: string) => {
        const trimmed = raw.trim();
        if (trimmed === "") {
            setOptions((prev) => {
                const next = [...prev];
                next[index] = Number.NaN;
                return next;
            });
            return;
        }
        const value = Number(trimmed);
        if (!Number.isFinite(value)) return;
        setOptions((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
        setFieldErrorsState((prev) =>
            clearFieldError(prev, "moneyManagementOptions")
        );
    };

    const handleRemoveOption = (index: number) => {
        if (options.length <= 1) {
            const optionErrors = {
                moneyManagementOptions: "Au moins une option est requise.",
            };
            setFieldErrorsState((prev) => ({ ...prev, ...optionErrors }));
            notifyFieldErrors(optionErrors, {
                message: "Au moins une option est requise.",
                type: "warning",
            });
            return;
        }
        setOptions((prev) => prev.filter((_, i) => i !== index));
        setFieldErrorsState((prev) =>
            clearFieldError(prev, "moneyManagementOptions")
        );
    };

    const handleSave = useCallback(async (): Promise<boolean> => {
        const base = Number(demoAccountBase);
        const challengeValue = Number(challenge);
        const cleaned = options.filter((n) => Number.isFinite(n) && n > 0);
        const nextErrors = setFieldErrors(
            [
                "demoAccountBase",
                (!Number.isFinite(base) || base <= 0) &&
                    "La base démo doit être un nombre positif.",
            ],
            [
                "challenge",
                (!Number.isFinite(challengeValue) || challengeValue <= 0) &&
                    "Le montant Challenge doit être un nombre positif.",
            ],
            [
                "moneyManagementOptions",
                cleaned.length === 0 &&
                    "Ajoute au moins une option de money management.",
            ]
        );
        setFieldErrorsState(nextErrors);
        const journalToastMessage = nextErrors.demoAccountBase
            ? "La base démo doit être un nombre positif."
            : nextErrors.challenge
              ? "Le montant Challenge doit être un nombre positif."
              : nextErrors.moneyManagementOptions
                ? "Ajoute au moins une option de money management."
                : undefined;
        if (
            notifyFieldErrors(nextErrors, {
                message: journalToastMessage,
                type: "warning",
            })
        ) {
            return false;
        }
        if (cleaned.some((n) => n === challengeValue)) {
            toast.warn(
                "Le Challenge ne doit pas figurer dans les options normales."
            );
            return false;
        }
        const unique = Array.from(new Set(cleaned)).sort((a, b) => a - b);
        if (unique.length !== cleaned.length) {
            toast.warn("Des montants sont en double.");
            return false;
        }

        setIsSaving(true);
        try {
            const saved = await patchAppSettings({
                journal: {
                    demoAccountBase: base,
                    moneyManagementOptions: unique,
                    challengeMoneyManagement: challengeValue,
                },
            });
            if (!saved?.journal) {
                throw new Error("EMPTY_SETTINGS");
            }
            const nextBase = String(saved.journal.demoAccountBase);
            const nextOpts = [
                ...saved.journal.moneyManagementOptions,
            ].sort((a, b) => a - b);
            const nextChallenge = String(
                saved.journal.challengeMoneyManagement
            );
            setDemoAccountBase(nextBase);
            setOptions(nextOpts);
            setChallenge(nextChallenge);
            setBaseline(journalSnapshot(nextBase, nextOpts, nextChallenge));
            setFieldErrorsState({});
            const { invalidateJournalSettingsCache } = await import(
                "../../../services/journalSettings"
            );
            invalidateJournalSettingsCache();
            toast.success("Paramètres enregistrés.");
            return true;
        } catch {
            toast.error("Échec de l'enregistrement.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [demoAccountBase, challenge, options]);

    useEffect(() => {
        registerUnsavedGuard({ isDirty, save: handleSave });
        return () => registerUnsavedGuard(null);
    }, [registerUnsavedGuard, isDirty, handleSave]);

    return (
        <div className={styles.form}>
            <div className={styles.header}>
                {isMobile ? (
                    <Select
                        value="journal"
                        className={styles.selectTab}
                        onChanged={(value) =>
                            handleTabChange(
                                value === "tags" ? "" : (value as string)
                            )
                        }
                    >
                        <option value="tags">Tags</option>
                        {canReadSettings && (
                            <option value="progression">Progression</option>
                        )}
                        {canReadSettings && (
                            <option value="journal">Journal</option>
                        )}
                    </Select>
                ) : (
                    <span className={styles.form_header}>Journal</span>
                )}
                <Button
                    label={isSaving ? "Enregistrement…" : "Enregistrer"}
                    size="medium"
                    variant="primary"
                    onClick={() => {
                        void handleSave();
                    }}
                    disabled={isLoading || isSaving}
                />
            </div>

            {isLoading ? (
                <Loader />
            ) : (
                <div className={styles.sections}>
                    <div className={styles.section}>
                        <span className={styles.form_name}>
                            Base compte démo ($)
                        </span>
                        <p className={styles.hint}>
                            Utilisée pour le % PnL des traders non financés. Les
                            traders financés utilisent leur capital sous
                            gestion.
                        </p>
                        <Input
                            type="number"
                            value={demoAccountBase}
                            onChanged={(value) => {
                                setDemoAccountBase(value);
                                setFieldErrorsState((prev) =>
                                    clearFieldError(prev, "demoAccountBase")
                                );
                            }}
                            error={fieldErrors.demoAccountBase}
                            className={styles.filter_input}
                        />
                    </div>

                    <div className={styles.section}>
                        <span className={styles.form_name}>
                            Money management
                        </span>
                        <p className={styles.hint}>
                            Options proposées à la création d&apos;une session
                            (hors Challenge).
                        </p>
                        <ul className={localStyles.optionList}>
                            {options.map((value, index) => (
                                <li
                                    key={`mm-option-${index}`}
                                    className={localStyles.optionRow}
                                >
                                    <div className={localStyles.optionEdit}>
                                        <Input
                                            type="number"
                                            value={
                                                Number.isFinite(value)
                                                    ? String(value)
                                                    : ""
                                            }
                                            onChanged={(raw) =>
                                                handleOptionChange(index, raw)
                                            }
                                            className={localStyles.optionInput}
                                            aria-label={`Money management ${index + 1}`}
                                        />
                                        <span className={localStyles.optionUnit}>
                                            €
                                        </span>
                                    </div>
                                    <IconButton
                                        icon={DeleteV2Icon}
                                        size="medium"
                                        color="secondary"
                                        onClick={() =>
                                            handleRemoveOption(index)
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                        <div className={localStyles.addRow}>
                            <Input
                                type="number"
                                placeholder="Nouveau montant"
                                value={newOption}
                                onChanged={(value) => {
                                    setNewOption(value);
                                    setFieldErrorsState((prev) =>
                                        clearFieldError(
                                            prev,
                                            "moneyManagementOptions"
                                        )
                                    );
                                }}
                                className={styles.filter_input}
                                error={fieldErrors.moneyManagementOptions}
                            />
                            <Button
                                label="Ajouter"
                                size="medium"
                                variant="secondary"
                                onClick={handleAddOption}
                            />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <span className={styles.form_name}>Challenge</span>
                        <p className={styles.hint}>
                            Une seule valeur, toujours affichée comme
                            «&nbsp;Challenge&nbsp;» dans le journal. Les
                            sessions avec ce montant sont exclues des stats.
                        </p>
                        <Input
                            type="number"
                            value={challenge}
                            onChanged={(value) => {
                                setChallenge(value);
                                setFieldErrorsState((prev) =>
                                    clearFieldError(prev, "challenge")
                                );
                            }}
                            error={fieldErrors.challenge}
                            className={styles.filter_input}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Journal;
